import { ReactElement, useLayoutEffect, useState } from "react";
import styled, { StyledComponent } from "styled-components";
import { useTabularMenuContext } from "./TabularMenuContext";
import { primaryActive } from "../../typography/alpha";
import { renderToString } from "react-dom/server";

type StyledTabularMenuProps = {
  largestTabWidth: number;
};

const StyledTabularMenuHorizontal = styled.div<StyledTabularMenuProps>`
  display: flex;
  > * {
    min-width: ${(props) => props.largestTabWidth}px;
  }
`;

const StyledTabularMenuVertical = styled.div<StyledTabularMenuProps>`
  display: inline-flex;
  > * {
    min-width: ${(props) => props.largestTabWidth}px;
  }
  flex-direction: column;
`;

const TabHorizontal = styled.button`
  position: relative;
  cursor: pointer;
  box-sizing: border-box;
  min-height: 32px;
  line-height: 16px;
  margin: 0px;
  padding: 8px;
  padding-right: 46px;
  text-align: left;
  ${primaryActive.subtitle3};
  background: var(--color-neutral-000-full);
  border: none;
  border-bottom: 2px solid var(--color-primary-200);
  :after {
    content: "";
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--color-primary-400);
    transform: scaleX(0);
    transition: 0.2s ease-in-out transform;
  }
  :focus {
    outline: none;
  }
  :hover,
  :focus {
    &:after {
      transform: scaleX(1);
    }
  }
`;

const ActiveTabHorizontal = styled(TabHorizontal)`
  cursor: default;
  background: var(--color-secondary-200);
  border-bottom-color: var(--color-secondary-600);
  /* stylelint-disable no-descending-specificity */
  :after {
    display: none;
  }
  /* stylelint-enable no-descending-specificity */
`;

const TabVertical = styled.button`
  position: relative;
  cursor: pointer;
  box-sizing: border-box;
  min-height: 32px;
  line-height: 16px;
  margin: 0px;
  padding: 8px;
  padding-right: 46px;
  text-align: left;
  ${primaryActive.subtitle3};
  color: var(--color-primary-900);
  font-weight: 500;

  background: var(--color-neutral-000-full);
  border: none;
  border-left: 3px solid rgba(0, 0, 0, 0);
  /* stylelint-disable no-descending-specificity */
  :after {
    content: "";
    position: absolute;
    left: -3px;
    top: 0;
    bottom: 0;
    width: 3px;
    background: var(--color-primary-400);
    transform: scaleX(0);
    transition: 0.2s ease-in-out transform;
  }
  /* stylelint-enable no-descending-specificity */
  :focus {
    outline: none;
  }
  :hover,
  :focus {
    &:after {
      transform: scaleX(1);
    }
  }
`;

const ActiveTabVertical = styled(TabVertical)`
  cursor: default;
  background: var(--color-secondary-200);
  border-left-color: var(--color-secondary-600);
  color: var(--color-primary-900);
  font-weight: 600;
  /* stylelint-disable no-descending-specificity */
  :after {
    display: none;
  }
  /* stylelint-enable no-descending-specificity */
`;

type useLargestTabWidthArgs = {
  menuElement: HTMLDivElement | null;
};

const useLargestTabWidth = ({ menuElement }: useLargestTabWidthArgs) => {
  const [largestTabWidth, setLargestTabWidth] = useState(0);
  /* eslint-disable react-hooks/exhaustive-deps */
  useLayoutEffect(() => {
    if (!menuElement) {
      return;
    }
    const tabs = Array.from(menuElement.children);
    const tabWidths = tabs.map((tab) => {
      const { width } = window.getComputedStyle(tab);
      const widthWithoutUnit = width.substring(0, width.length - 2);
      return Number.parseInt(widthWithoutUnit, 10);
    });
    setLargestTabWidth(Math.max(...tabWidths));
  });
  /* eslint-enable react-hooks/exhaustive-deps */
  return {
    largestTabWidth,
  };
};

type TabularMenuProps = {
  tabOrientation?: "horizontal" | "vertical";
  className?: string;
  testId?: string;
  id?: string;
};

export enum TabularMenuTestIds {
  Container = "tabular-menu",
}

export const TabularMenu = ({
  tabOrientation = "horizontal",
  className,
  testId,
  id,
}: TabularMenuProps): JSX.Element => {
  const { tabs, activeTabIndex, setActiveTabIndex } = useTabularMenuContext();
  const [menuElement, setMenuElement] = useState<HTMLDivElement | null>(null);
  const { largestTabWidth } = useLargestTabWidth({ menuElement });

  let StyledTabularMenu: StyledComponent<
    "div",
    Record<string, unknown>,
    StyledTabularMenuProps
  >;
  let ActiveTab: StyledComponent<"button", Record<string, unknown>>;
  let Tab: StyledComponent<"button", Record<string, unknown>>;

  if (tabOrientation === "vertical") {
    StyledTabularMenu = StyledTabularMenuVertical;
    ActiveTab = ActiveTabVertical;
    Tab = TabVertical;
  } else {
    StyledTabularMenu = StyledTabularMenuHorizontal;
    ActiveTab = ActiveTabHorizontal;
    Tab = TabHorizontal;
  }

  return (
    <StyledTabularMenu
      largestTabWidth={largestTabWidth}
      ref={setMenuElement}
      className={className}
      data-testid={testId ?? TabularMenuTestIds.Container}
      id={id}
    >
      {tabs.map((tab, index) => {
        const tabIsActive = index === activeTabIndex;
        const TabComponent = tabIsActive ? ActiveTab : Tab;
        const onClick = tabIsActive
          ? () => {}
          : () => {
              if (tab.onClick) {
                tab.onClick();
              }
              setActiveTabIndex(index);
            };

        return (
          <TabComponent
            key={renderToString(tab.label as ReactElement)}
            onClick={onClick}
          >
            {tab.label as JSX.Element}
          </TabComponent>
        );
      })}
    </StyledTabularMenu>
  );
};
